import type {ColorName} from 'shells/colors/colors';

import {OpenableModals, type OpenableModalId} from '../openable-modals/openable-modals';

export interface LearnMoreConfig {
    openableModalId: OpenableModalId;
    label?: string;
}

export interface OnDismissConfig {
    openableModalId: OpenableModalId;
}

/** Partial display props for admin overrides */
export interface FeedbackSurveyAdminProps {
    bannerTitle?: string;
    surveyTitle?: string;
    surveySubtitle?: string;
    feedbackPrompt?: string;
    surveyImage?: string;
    surveyImageBackgroundColor?: ColorName;
}

export interface FeedbackSurvey {
    id: string;
    pathNames: Array<string>;
    /**
     * Name of the Linear project this survey relates to. Shown on the
     * auto-created Linear feedback issue (in the Post launch feedback team's
     * Triage queue) so it can be routed to the right project. Optional — falls
     * back to the survey id if unset.
     */
    linearProjectName?: string;
    isEnabled?: () => boolean;
    expireDate?: string;
    learnMore?: LearnMoreConfig;
    onDismiss?: OnDismissConfig;
    /** Default display props (shown to non-admins, and admins unless overridden) */
    bannerTitle: string;
    surveyTitle: string;
    surveySubtitle: string;
    feedbackPrompt?: string;
    surveyImage?: string;
    surveyImageBackgroundColor?: ColorName;
    /** Whether to animate the banner when it is shown. Defaults to true. */
    shouldAnimate?: boolean;
    /** Optional overrides for admin users - only specify props that differ */
    adminProps?: FeedbackSurveyAdminProps;
}

/*
    - use * as a wildcard to match certain dynamic paths (ex: /lead/* would match /lead/123, /lead/456, etc.)
    - use \n to create line breaks in the surveyTitle and surveySubtitle
    - learnMore is optional and can be used to open an onboarding modal when the user clicks "Learn more"
    - adminProps can override any display prop for admin users (only specify what differs)
    - isEnabled can be used to gate a survey behind a runtime flag check
*/
export const FEEDBACK_SURVEYS: Array<FeedbackSurvey> = [
    {
        id: 'email-digests',
        pathNames: ['/my-account/email-digests'],
        linearProjectName: 'Email digest V2',
        // Default (shown to non-admins)
        bannerTitle: 'Email digests: receive actionable insights on your next steps',
        surveyTitle: 'Tell us what you think about the new email digests',
        surveySubtitle:
            'Did you find the email useful? Did you encounter any difficulties viewing the email? Is there anything you wish was added or removed from the email?',
        surveyImage: '/include/images/onboarding/email-digests.svg',
        feedbackPrompt: 'Tell us what you think',
        // Admin override - only what's different
        adminProps: {
            bannerTitle: 'Email digests: receive important updates about your team’s performance',
        },
        expireDate: '2026-02-14',
    },
    {
        id: 'chatbot-feedback',
        pathNames: ['/reports/chatbot'],
        linearProjectName: 'Chatbot knowledge improvements',
        bannerTitle: 'Chatbot report: see how your chatbot is performing',
        surveyTitle: 'Tell us what you think about the new chatbot report',
        surveySubtitle:
            'Did you find the chatbot report useful? Is there anything you wish was added or removed from the report?',
        surveyImage: '/include/illustrations/chatbot-report.svg',
        surveyImageBackgroundColor: 'blueXlt',
        feedbackPrompt: 'Tell us what you think',
        expireDate: '2026-04-01',
    },
    {
        id: 'peep-redesign-force-opt',
        pathNames: ['/person/*', '/company/*'],
        linearProjectName: 'Peep page feedback Q3 2026',
        isEnabled: () => NutClientConfig.hasPeepPagesRedesignForceOptBanner,
        bannerTitle: 'Updated people and company pages are coming!',
        surveyTitle: 'Tell us what you think about the new people and company pages',
        surveySubtitle:
            'Did you find the updated page easy to use? Was anything harder to find? Are there any issues or improvements you’d suggest?',
        surveyImage: '/include/illustrations/peeps-onboarding.svg',
        surveyImageBackgroundColor: 'blueXlt',
        feedbackPrompt: 'Share feedback',
        learnMore: {
            openableModalId: OpenableModals.PEEP_REDESIGN_ONBOARDING_MODAL,
            label: 'Learn more',
        },
        expireDate: '2026-09-15',
        shouldAnimate: false,
    },
    // Survey accessible to everyone as it's used for our general user avatar
    // "Lead feedback" option
    {
        id: 'peep-redesign-general',
        pathNames: [],
        linearProjectName: 'Peep page feedback Q3 2026',
        bannerTitle: 'Updated people and company pages are coming!',
        surveyTitle: 'Tell us what you think about the new people and company pages',
        surveySubtitle:
            'Did you find the updated page easy to use? Was anything harder to find? Are there any issues or improvements you’d suggest?',
        surveyImage: '/include/illustrations/peeps-onboarding.svg',
        surveyImageBackgroundColor: 'blueXlt',
        feedbackPrompt: 'Share feedback',
        expireDate: '2026-09-15',
    },
    {
        id: 'shared-email-inbox',
        pathNames: ['/inbox/email', '/inbox/email/*'],
        isEnabled: () => NutClientConfig.hasSharedEmailInboxFF,
        bannerTitle: 'New shared email inbox is here!',
        surveyTitle: 'Tell us what you think about the new shared email inbox',
        surveySubtitle:
            'What do you think about the new shared email inbox? Are there any additional features or changes you would suggest for the inbox?',
        surveyImage: '/include/illustrations/shared-email-inbox-feedback-banner.svg',
        surveyImageBackgroundColor: 'blueXlt',
        feedbackPrompt: 'Share feedback',
        learnMore: {
            openableModalId: OpenableModals.SHARED_EMAIL_INBOX_LEARN_MORE_MODAL,
            label: 'Learn more',
        },
        expireDate: '2026-05-03',
        shouldAnimate: false,
    },
    {
        id: 'duplicates-page',
        pathNames: ['/setup/manage-duplicates/*'],
        bannerTitle: 'Manage your people and company duplicates all in one place!',
        surveyTitle: 'Tell us what you think about the new duplicates page',
        surveySubtitle:
            'What do you think about the new duplicates page? Are there any additional features or changes you would suggest?',
        surveyImageBackgroundColor: 'blueXlt',
        feedbackPrompt: 'Share feedback',
        expireDate: '2026-06-13',
        shouldAnimate: false,
    },
    {
        id: 'installments-report',
        pathNames: ['/reports/installments/*'],
        linearProjectName: 'Lead value installments',
        isEnabled: () => NutClientConfig.hasInstallmentsReport,
        bannerTitle: 'Installments report is here!',
        surveyTitle: 'Tell us what you think about the new installments report',
        surveySubtitle:
            'What do you think about installment payments or the report? Are there any additional features or changes you would suggest?',
        feedbackPrompt: 'Share feedback',
        expireDate: '2026-07-14',
        shouldAnimate: false,
    },
    {
        id: 'smart-sequences',
        pathNames: ['/marketing/smart-sequences'],
        bannerTitle: 'Smart sequences are here!',
        feedbackPrompt: 'Share feedback',
        linearProjectName: 'Smart Sequences',
        surveyImage: '/include/illustrations/smart-sequences/survey.svg',
        learnMore: {
            openableModalId: OpenableModals.SMART_SEQUENCES_ONBOARDING_MODAL,
            label: 'Learn more',
        },
        surveyTitle: 'Tell us what you think about Smart sequences',
        surveySubtitle:
            'Did you find the updated page easy to use, or was anything confusing or harder to find? Any issues or improvements you’d suggest?',
    },
];
